import { listAICatalogModels } from "@x12i/ai-profiles";
import type { CatalogModelSummary, RegistrySourceMode } from "@x12i/ai-profiles";
import { AiModelsCatalogClient } from "../catalog/AiModelsCatalogClient.js";
import type { AiModelsCatalogClientOptions } from "../catalog/AiModelsCatalogClient.js";
import type { AiModelRecord, ModelListFilters, ModelListResult } from "./types.js";
import {
  resolveModelVendor,
  type ModelVendorRef,
  type ResolveModelVendorOptions,
} from "../cost/resolveModelVendor.js";

export type AiModelsServiceOptions = AiModelsCatalogClientOptions;

function catalogSource(options: AiModelsServiceOptions): RegistrySourceMode {
  if (options.bundledOnly) return "bundled";
  return options.source ?? "auto";
}

function hydrateFromSummaries(
  summaries: CatalogModelSummary[],
  all: Map<string, AiModelRecord>,
): AiModelRecord[] {
  const out: AiModelRecord[] = [];
  const seen = new Set<string>();
  for (const s of summaries) {
    const candidates = [
      `${s.provider}/${s.modelId}`,
      s.modelId,
      s.openRouterModelId,
    ];
    let record: AiModelRecord | undefined;
    for (const id of candidates) {
      const hit = all.get(id);
      if (!hit) continue;
      if (hit.providerId === s.provider) {
        record = hit;
        break;
      }
      record ??= hit;
    }
    if (record && !seen.has(record.modelId)) {
      seen.add(record.modelId);
      out.push(record);
    }
  }
  return out;
}

/**
 * High-level model catalog API — list, filter, count, and get full model info.
 * Attribute filters are delegated to `@x12i/ai-profiles` {@link listAICatalogModels}.
 */
export class AiModelsService {
  private readonly client: AiModelsCatalogClient;
  private readonly options: AiModelsServiceOptions;

  constructor(options: AiModelsServiceOptions = {}) {
    this.options = options;
    this.client = new AiModelsCatalogClient(options);
  }

  async getAllModels(): Promise<Map<string, AiModelRecord>> {
    return this.client.getAllModels();
  }

  private async listSummaries(
    filters: Omit<ModelListFilters, "limit" | "offset" | "supportedParameter">,
  ): Promise<CatalogModelSummary[]> {
    const source = catalogSource(this.options);
    return listAICatalogModels({
      source,
      ...(source !== "auto" ? { refresh: true as const } : {}),
      provider: filters.providerId,
      status: filters.status,
      inputModality: filters.inputModality,
      outputModality: filters.outputModality,
      supportsTools: filters.supportsTools,
      supportsReasoning: filters.supportsReasoning,
      search: filters.search,
    });
  }

  private async filteredRecords(filters: ModelListFilters = {}): Promise<AiModelRecord[]> {
    const summaries = await this.listSummaries(filters);
    const all = await this.getAllModels();
    let models = hydrateFromSummaries(summaries, all);

    if (filters.supportedParameter) {
      const param = filters.supportedParameter;
      models = models.filter((m) => m.supportedParameters.includes(param));
    }

    models.sort((a, b) => a.name.localeCompare(b.name));
    return models;
  }

  async listModels(filters: ModelListFilters = {}): Promise<ModelListResult> {
    const allMatching = await this.filteredRecords(filters);
    const total = allMatching.length;
    const limit = filters.limit ?? 50;
    const offset = filters.offset ?? 0;
    const models = allMatching.slice(offset, offset + limit);
    return { models, total, limit, offset };
  }

  async countModels(filters: Omit<ModelListFilters, "limit" | "offset"> = {}): Promise<number> {
    const models = await this.filteredRecords(filters);
    return models.length;
  }

  async getModelInfo(modelIdOrAlias: string, provider?: string): Promise<AiModelRecord | null> {
    return this.client.getModel(modelIdOrAlias, provider);
  }

  async resolve(
    modelIdOrAlias: string,
    provider?: string,
  ): Promise<import("../sync/modelNameResolver/types.js").ModelResolutionResult> {
    return this.client.resolveModel({ model: modelIdOrAlias, provider });
  }

  async resolveVendor(
    model: string,
    options?: ResolveModelVendorOptions,
  ): Promise<ModelVendorRef | null> {
    return resolveModelVendor(model, this.client, options);
  }

  async refresh(): Promise<void> {
    await this.client.refresh();
  }
}

export async function getModelInfo(
  modelIdOrAlias: string,
  options: AiModelsServiceOptions = {},
  provider?: string,
): Promise<AiModelRecord | null> {
  return new AiModelsService(options).getModelInfo(modelIdOrAlias, provider);
}
